import { useCallback, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useCurrentAuthenticatedUser } from 'src/app/shared/hooks/session';
import type { MutationState } from 'src/app/shared/utils/async';

import { editPublishedPetition } from './api';
import type { PetitionInput, PetitionResponse } from './types';

type RequestState = MutationState<{ data: PetitionResponse }, { error: string }>;
type UseEditPublishedPetitionOptions = {
	onSuccess?: (petition: PetitionResponse, { editedProperties }: { editedProperties: string[] }) => void;
	onError?: (error: string) => void;
};
type UseEditPublishedPetitionReturn = RequestState & {
	mutate: (petition: PetitionInput) => Promise<Extract<RequestState, { status: 'loaded' } | { status: 'error' }>>;
	reset: () => void;
};

export function useEditPublishedPetition({
	onSuccess,
	onError,
}: UseEditPublishedPetitionOptions = {}): UseEditPublishedPetitionReturn {
	const utilityContext = useUtilityContext();
	const user = useCurrentAuthenticatedUser();

	const [state, setState] = useState<RequestState>({ status: 'idle' });

	const handleEditPublishedPetition = useCallback(
		async (petitionInput: PetitionInput) => {
			setState({ status: 'loading' });

			try {
				const result = await editPublishedPetition({ petitionInput, user }, utilityContext);
				const editedProperties = Object.keys(petitionInput).filter((field) => field !== 'id');
				const successStatus = { status: 'loaded' as const, data: result };
				setState(successStatus);
				onSuccess?.(result, { editedProperties });
				return successStatus;
			} catch (e) {
				const errorMessage = (e as Error).message;
				const errorStatus = { status: 'error' as const, error: errorMessage };
				setState(errorStatus);
				onError?.(errorMessage);
				return errorStatus;
			}
		},
		[user, onError, onSuccess, utilityContext],
	);

	const handleReset = useCallback(() => {
		setState({ status: 'idle' });
	}, []);

	return { mutate: handleEditPublishedPetition, reset: handleReset, ...state };
}
